import { useEffect, useMemo, useState } from "react";
import { useVideoPreviewThumbnails } from "@circle-react/components/shared/MediaPlayer/VideoPlayer/hooks/useVideoPreviewThumbnails";
import {
  getChapterThumbnailForTime,
  parseThumbnailVtt,
} from "../utils/thumbnailVttParser";

interface Chapter {
  start_time: number;
  title: string;
}

type ChapterThumbnails = Record<string, string | null>;

interface UseChapterThumbnailsProps {
  chapters: Chapter[];
  previewThumbnailsUrl?: string;
}

export const useChapterThumbnails = ({
  chapters,
  previewThumbnailsUrl,
}: UseChapterThumbnailsProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [chapterThumbnails, setChapterThumbnails] = useState<ChapterThumbnails>(
    {},
  );

  const { vttContent, isLoading: isLoadingPreviewThumbnails } =
    useVideoPreviewThumbnails({ previewThumbnailsUrl });

  const thumbnailVtt = useMemo(
    () => (vttContent ? parseThumbnailVtt(vttContent) : null),
    [vttContent],
  );

  useEffect(() => {
    let isMounted = true;

    if (isLoadingPreviewThumbnails) return;

    if (!thumbnailVtt?.cues.length || !thumbnailVtt?.imageUrl) {
      setIsLoading(false);
      return;
    }

    const { cues, imageUrl } = thumbnailVtt;

    const extractChapterThumbnails = async () => {
      try {
        const chapterThumbnailEntries = await Promise.all(
          chapters.map(async chapter => {
            const thumbnail = await getChapterThumbnailForTime({
              cues,
              time: chapter.start_time,
              thumbnailUrl: imageUrl,
            });

            return [String(chapter.start_time), thumbnail] as const;
          }),
        );

        if (isMounted) {
          setChapterThumbnails(Object.fromEntries(chapterThumbnailEntries));
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted && error instanceof Error) {
          console.error("Failed to extract chapter thumbnails:", error);
          setIsLoading(false);
        }
      }
    };

    void extractChapterThumbnails();

    return () => {
      isMounted = false;
    };
  }, [isLoadingPreviewThumbnails, thumbnailVtt, chapters]);

  return { chapterThumbnails, isLoading };
};
